<template>
  <div>
    <UserGroupField
      :updateSelection="
        (v) => {
          principalIds = v
        }
      "
      :selection="principalIds"
    />
    <v-tabs v-model="lang">
      <v-tab :class="{ 'red--text': hasEnError }">EN</v-tab>
      <v-tab :class="{ 'red--text': hasFrError }">FR</v-tab>
      <v-tab-item>
        <v-text-field
          v-model="enSubject"
          :label="$t('subject')"
          :error-messages="enSubjectErrors"
          @input="$v.form.en_subject.$touch()"
          @blur="$v.form.en_subject.$touch()"
        />
        <tiptap-vuetify
          v-model="enMessage"
          :extensions="extensions"
          :placeholder="$t('message')"
          :toolbar-attributes="{ color: enMessageErrors.length > 0 ? 'error lighten-3' : 'grey lighten-3' }"
          :card-props="{ outlined: true }"
          class="mb-4"
        >
          <template v-slot:toolbar-after>
            <div class="text-caption font-italic grey--text pa-1">
              {{ hint }}
            </div>
          </template>
          <template v-slot:footer v-if="enMessageErrors.length > 0">
            <v-divider class="error" />
            <div class="text-caption error--text">{{ enMessageErrors[0] }}</div>
          </template>
        </tiptap-vuetify>
      </v-tab-item>
      <v-tab-item>
        <v-text-field
          v-model="frSubject"
          :label="$t('subject')"
          :error-messages="frSubjectErrors"
          @input="$v.form.fr_subject.$touch()"
          @blur="$v.form.fr_subject.$touch()"
        />
        <tiptap-vuetify
          v-model="frMessage"
          :extensions="extensions"
          :placeholder="$t('message')"
          :toolbar-attributes="{ color: frMessageErrors.length > 0 ? 'error lighten-3' : 'grey lighten-3' }"
          :card-props="{ outlined: true }"
          class="mb-4"
        >
          <template v-slot:toolbar-after>
            <div class="text-caption font-italic grey--text pa-1">
              {{ hint }}
            </div>
          </template>
          <template v-slot:footer v-if="frMessageErrors.length > 0">
            <v-divider class="error" />
            <div class="text-caption error--text">{{ frMessageErrors[0] }}</div>
          </template>
        </tiptap-vuetify>
      </v-tab-item>
    </v-tabs>
  </div>
</template>

<script>
// This action form extension is added to action forms that send text/email messages
import { isEmpty } from 'lodash/lang'
import {
  TiptapVuetify,
  Heading,
  Bold,
  Italic,
  Blockquote,
  ListItem,
  BulletList,
  OrderedList,
  HorizontalRule
} from 'tiptap-vuetify'
import { UserGroupField } from '@argon/iam/components'
import { BaseForm } from '@argon/app/components'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'SendMessageExtension',
  extends: BaseForm,
  components: { UserGroupField, TiptapVuetify },
  props: {
    item: Object,
    updateData: { type: Function, required: true },
    setError: { type: Function, required: true }
  },
  validations: {
    form: {
      fr_subject: { required },
      en_subject: { required },
      fr_message: { required },
      en_message: { required }
    }
  },
  data: () => ({
    form: {
      fr_subject: '',
      en_subject: '',
      fr_message: '',
      en_message: '',
      principal_ids: []
    },
    lang: 0,
    extensions: [
      Bold,
      Italic,
      [
        Heading,
        {
          options: {
            levels: [1, 2, 3]
          }
        }
      ],
      ListItem,
      Blockquote,
      BulletList,
      OrderedList,
      HorizontalRule
    ]
  }),
  computed: {
    hint() {
      return `${this.$t('placeholders')} {URL}, {USER}, {ENTITY}, {STATE}`
    },
    hasEnError() {
      return this.$v.form.en_subject.$error || this.$v.form.en_message.$error
    },
    enSubjectErrors() {
      const errors = []
      if (!this.$v.form.en_subject.$dirty) return errors
      if (!this.$v.form.en_subject.required) {
        errors.push(this.$t('required'))
        this.setError(true)
      } else {
        this.setError(false)
      }
      this.getServerErrors('en_subject', errors)
      return errors
    },
    enSubject: {
      get() {
        return this.form.en_subject
      },
      set(value) {
        this.form['en_subject'] = value
        this.updateData(this.form)
      }
    },
    enMessageErrors() {
      const errors = []
      if (!this.$v.form.en_message.$dirty) return errors
      if (!this.$v.form.en_message.required) {
        errors.push(this.$t('required'))
        this.setError(true)
      } else {
        this.setError(false)
      }
      this.getServerErrors('en_message', errors)
      return errors
    },
    enMessage: {
      get() {
        return this.form.en_message
      },
      set(value) {
        this.form['en_message'] = value
        this.updateData(this.form)
      }
    },
    hasFrError() {
      return this.$v.form.fr_subject.$error || this.$v.form.fr_message.$error
    },
    frSubjectErrors() {
      const errors = []
      if (!this.$v.form.fr_subject.$dirty) return errors
      if (!this.$v.form.fr_subject.required) {
        errors.push(this.$t('required'))
        this.setError(true)
      } else {
        this.setError(false)
      }
      this.getServerErrors('fr_subject', errors)
      return errors
    },
    frSubject: {
      get() {
        return this.form.fr_subject
      },
      set(value) {
        this.form['fr_subject'] = value
        this.updateData(this.form)
      }
    },
    frMessageErrors() {
      const errors = []
      if (!this.$v.form.fr_message.$dirty) return errors
      if (!this.$v.form.fr_message.required) {
        errors.push(this.$t('required'))
        this.setError(true)
      } else {
        this.setError(false)
      }
      this.getServerErrors('fr_message', errors)
      return errors
    },
    frMessage: {
      get() {
        return this.form.fr_message
      },
      set(value) {
        this.form['fr_message'] = value
        this.updateData(this.form)
      }
    },
    principalIds: {
      get() {
        return this.form.principal_ids
      },
      set(value) {
        this.form.principal_ids = value
        this.updateData(this.form)
      }
    }
  },
  methods: {
    updateSelection(value) {
      this.form.principal_ids = value
    }
  },
  created() {
    if (this.item) {
      this.principalIds = this.item.data && this.item.data.principal_ids ? this.item.data.principal_ids : []
      this.enSubject = this.item.data && this.item.data.en_subject ? this.item.data.en_subject : ''
      this.enMessage = this.item.data && this.item.data.en_message ? this.item.data.en_message : ''
      this.frSubject = this.item.data && this.item.data.fr_subject ? this.item.data.fr_subject : ''
      this.frMessage = this.item.data && this.item.data.fr_message ? this.item.data.fr_message : ''
    }
    this.$v.form.en_subject.$touch()
    this.$v.form.fr_subject.$touch()
    this.$v.form.en_message.$touch()
    this.$v.form.fr_message.$touch()
  }
}
</script>
<i18n>
{
  "en": {
    "message": "Message",
    "placeholders": "Available placeholders: ",
    "required": "@:labels.required",
    "subject": "Subject"
  },
  "fr": {
    "message": "Message",
    "placeholders": "Balises disponibles: ",
    "required": "@:labels.required",
    "subject": "Sujet"
  }
}
</i18n>
